import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {ButtonMover} from 'shells/button-mover/button-mover';
import {ButtonCard} from 'shells/card/button-card';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {OutreachIcon} from 'shells/icon/svg/outreach-icon';
import {UpgradePillButton} from 'shells/limits/upgrade-pill-button';
import {Link} from 'shells/link/link';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {Body} from 'shells/typography/body';
import {Title} from 'shells/typography/title';

import {useGetUsageStats} from '../../email-marketing/graphql/hooks/use-get-usage-stats';
import {BulkPersonalEmailFeatureGate} from '../../limits/feature-gates/bulk-personal-email-feature-gate';

import {getEmailStats} from './helpers';
import type {EmailStats} from './helpers';
import type {SendFromListOption} from './send-from-list-modal';

import styles from './send-from-list-selection-modal.module.css';

export type Props = {
    onClose: () => void;
    userEmail: string | null | undefined;
    selectedCount: number;
    canAccessMarketing: boolean;
};

type OwnProps = Props & {
    onSelectOption: (arg1: SendFromListOption) => void;
    isLoadingRelatedEmails: boolean;
};

export function SendFromListSelectionModal(props: OwnProps) {
    const [selectedOption, setSelectedOption] = React.useState<
        SendFromListOption | null | undefined
    >(undefined);
    // It is possible for options to not be selectable, so we can only auto select the ones that are.
    const [selectableOptions, setSelectableOptions] = React.useState<Array<SendFromListOption>>([]);
    const [isFeatureGateModalOpen, setIsFeatureGateModalOpen] = React.useState(false);

    if (selectableOptions.length && !selectedOption) {
        setSelectedOption(selectableOptions[0]);
    }

    // Get Nutshell Marketing usage data for checking to see if selected emails can be sent
    const {nutshellMarketingStats, isLoading} = useGetUsageStats();

    const emailStats = getEmailStats(nutshellMarketingStats);

    function addSelectableOption(option: SendFromListOption) {
        setSelectableOptions((prevState) => [...prevState, option]);
    }

    return (
        <>
            <Modal
                isOpen={true}
                headerText='What kind of email would you like to send?'
                closeButton={true}
                onClose={props.onClose}
                maxHeight={500}
            >
                <div className={styles['modal-content']}>
                    <div className={styles['options-container']}>
                        {!isLoading && !props.isLoadingRelatedEmails && emailStats ? (
                            <>
                                {getPersonalEmailOptionCard(
                                    props.userEmail,
                                    props.selectedCount,
                                    selectedOption,
                                    setSelectedOption,
                                    addSelectableOption,
                                    setIsFeatureGateModalOpen
                                )}
                                {getMarketingOutreachOptionCard(
                                    props.canAccessMarketing,
                                    emailStats,
                                    props.selectedCount,
                                    selectedOption,
                                    setSelectedOption,
                                    addSelectableOption
                                )}
                            </>
                        ) : (
                            <LoadingPage />
                        )}
                    </div>
                    <FooterBar
                        left={
                            <ButtonMover size='big' direction='left'>
                                <Button onClick={props.onClose} variant='text' size='big'>
                                    Cancel
                                </Button>
                            </ButtonMover>
                        }
                        right={
                            // The button is disabled until an option is selected
                            <Button
                                onClick={() => {
                                    if (
                                        selectedOption &&
                                        selectableOptions.includes(selectedOption)
                                    )
                                        props.onSelectOption(selectedOption);
                                }}
                                variant='secondary'
                                size='big'
                                disabled={!selectedOption}
                            >
                                Next
                            </Button>
                        }
                    />
                </div>
            </Modal>
            <BulkPersonalEmailFeatureGate
                isOpen={isFeatureGateModalOpen}
                setIsOpen={setIsFeatureGateModalOpen}
            />
        </>
    );
}

function getPersonalEmailOptionCard(
    userEmail: string | null | undefined,
    selectedCount: number,
    selectedOption: string | null | undefined,
    setSelectedOption: (arg1: SendFromListOption) => void,
    addSelectableOption: (arg1: SendFromListOption) => void,
    setIsFeatureGateModalOpen: (arg1: boolean) => void
) {
    const title = 'Personal email';
    const hasBulkPersonalEmails = NutClientConfig.hasBulkPersonalEmails;

    if (!hasBulkPersonalEmails) {
        return (
            <OptionCard
                title={title}
                subtitle='Connect your email to send personal emails directly in Nutshell'
                icon={<EmailIcon size={32} />}
                link={
                    // @ts-expect-error
                    <UpgradePillButton
                        onClick={() => {
                            setIsFeatureGateModalOpen(true);
                        }}
                    >
                        Upgrade
                    </UpgradePillButton>
                }
                disabled={false}
            />
        );
    }

    if (!userEmail) {
        // (error) User does not have personal email configured
        return (
            <OptionCard
                title={title}
                subtitle='Connect your email to send personal emails directly in Nutshell'
                icon={<EmailIcon size={32} />}
                link={
                    // @ts-expect-error
                    <Link variant='primary' to='/my-account/email-sending' as={RoutedLink}>
                        Connect
                    </Link>
                }
                disabled={true}
            />
        );
    } else {
        // Defaults if there is no error
        let subtitle = `Send plain-text emails from ${userEmail}`;
        let isDisabled = false;

        if (selectedCount > 500) {
            // (error) User has selected more than 500 people
            // @ts-expect-error
            subtitle = (
                <span className={styles.error}>
                    You can only send personal emails to 500 recipients at a time
                </span>
            );
            isDisabled = true;
        }

        // If there is currently no selected option and this one is selectable, add it to options list
        if (!selectedOption && !isDisabled) {
            addSelectableOption('personal');
        }

        return (
            <OptionCard
                title={title}
                subtitle={subtitle}
                icon={<EmailIcon size={32} />}
                isSelected={selectedOption === 'personal'}
                onClick={() => setSelectedOption('personal')}
                disabled={isDisabled}
            />
        );
    }
}

function getMarketingOutreachOptionCard(
    userCanAccessMarketing: boolean,
    emailStats: EmailStats,
    selectedCount: number,
    selectedOption: string | null | undefined,
    setSelectedOption: (arg1: SendFromListOption) => void,
    addSelectableOption: (arg1: SendFromListOption) => void
) {
    const title = 'Marketing outreach';
    let subtitle = 'Design & send messages to thousands of contacts';
    let isDisabled = false;

    if (!userCanAccessMarketing) {
        // (error) User does not have access to nutshell marketing
        subtitle = 'Ask your account admin for access to start using these features';
        isDisabled = true;
    } else if (
        typeof emailStats.remainingEmails === 'number' &&
        emailStats.remainingEmails <= selectedCount
    ) {
        // (error) User needs to upgrade nutshell marketing plan before sending
        // @ts-expect-error
        subtitle = (
            <span className={styles.error}>You’ll need to upgrade before sending this message</span>
        );
        isDisabled = true;
    }

    // If there is currently no selected option and this one is selectable, add it to options list
    if (!selectedOption && !isDisabled) {
        addSelectableOption('marketing');
    }

    return (
        <OptionCard
            title={title}
            subtitle={subtitle}
            icon={<OutreachIcon size={32} />}
            isSelected={selectedOption === 'marketing'}
            onClick={() => setSelectedOption('marketing')}
            disabled={isDisabled}
        />
    );
}

const OptionCard = (props: {
    title: string;
    subtitle: string | React.ReactNode;
    icon: React.ReactNode;
    link?: React.ReactNode;
    isSelected?: boolean;
    onClick?: () => void;
    disabled: boolean;
}) => (
    <ButtonCard isSelected={props.isSelected} onClick={props.onClick} isDisabled={props.disabled}>
        <div className={styles['option-card']}>
            <div>
                <div className={styles['icon-container']}>{props.icon}</div>
                <div>
                    <Title>{props.title}</Title>
                    <Body>{props.subtitle}</Body>
                </div>
            </div>
            {props.link ? props.link : undefined}
        </div>
    </ButtonCard>
);
